import React, { FC, Fragment, useRef } from 'react';
import {
	GENERAL_FORM_FIELDS,
	GENERAL_FORM_FIELDS_NAMES,
	FUNDING_CONFIG_FORM_FIELDS,
	FUNDING_CONFIG_FORM_FIELDS_NAMES,
	ISwitchersProps, PayoutFrequencyType
} from '../../constants/merchant/general-form';
import { Title } from '../developers/page-components/Title';
import { GeneralSwitches } from './GeneralSwitches';
import { useTranslation } from 'react-i18next';
import { FormInput } from './FormInput';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { InputType, formInputFieldName } from '../form/form-input-constants';
import { SelectPicker } from 'rsuite';

interface Props {
	title: string;
	items: ISwitchersProps[];
	formik: any;
	isEditMode: boolean;
}

const payoutFrequencyOptions = [
	{ label: 'Daily', value: PayoutFrequencyType.DAILY },
	{ label: 'Weekly', value: PayoutFrequencyType.WEEKLY },
	{ label: 'Twice a Month', value: PayoutFrequencyType.TWICEAMONTH },
	{ label: 'Monthly', value: PayoutFrequencyType.MONTHLY }
];

export const GeneralCategories = (props: Props): JSX.Element => {
	const { title, items, formik, isEditMode } = props;
	const { t } = useTranslation();
	const { merchantState: { merchantsLists }} = useMerchants();
	const supportedCountriesRef = useRef(null);

	const getSwitcher = (
		title: string,
		formName: GENERAL_FORM_FIELDS | FUNDING_CONFIG_FORM_FIELDS,
		description: GENERAL_FORM_FIELDS_NAMES | FUNDING_CONFIG_FORM_FIELDS_NAMES
	): JSX.Element => {

		if (formName === FUNDING_CONFIG_FORM_FIELDS.PAYOUT_FREQUENCY) {
			return (
				<FormInput
					formik={formik}
					fieldName={formName as formInputFieldName}
					label={t(title)}
					placeholder={t(title)}
					inputType={InputType.Select}
					data={payoutFrequencyOptions}
					searchable={false}
					isEditMode={isEditMode}
					labelAdditionalClasses="fs-7"
					fakeInputLabelAdditionalClasses="fs-7"
					additionalClasses="col-md-12"
					fakeInputAdditionalClass="col-md-12"
					isLabelIcon
					tooltipText={t(description)}
					key={formName}
				/>
			);
		}

		switch (title) {
			case GENERAL_FORM_FIELDS_NAMES.CONSUMER_NOTIFICATION_CHANNEL:
				return (
					<FormInput
						formik={formik}
						fieldName={GENERAL_FORM_FIELDS.CONSUMER_NOTIFICATION_CHANNEL}
						label={t(GENERAL_FORM_FIELDS_NAMES.CONSUMER_NOTIFICATION_CHANNEL)}
						placeholder={t(GENERAL_FORM_FIELDS_NAMES.CONSUMER_NOTIFICATION_CHANNEL)}
						inputType={InputType.Select}
						data={merchantsLists?.consumerNotificationChannelValues}
						searchable={false}
						isEditMode={isEditMode}
						labelAdditionalClasses="fs-7"
						fakeInputLabelAdditionalClasses="fs-7"
						additionalClasses="col-md-12"
						fakeInputAdditionalClass="col-md-12"
						isLabelIcon
						tooltipText={t(GENERAL_FORM_FIELDS_NAMES.CONSUMER_NOTIFICATION_CHANNEL_DESCRIPTION)}
						key={GENERAL_FORM_FIELDS.CONSUMER_NOTIFICATION_CHANNEL}
					/>
				);
			case GENERAL_FORM_FIELDS_NAMES.SUPPORTED_COUNTRIES:
				return (
					<FormInput
						formik={formik}
						fieldName={GENERAL_FORM_FIELDS.SUPPORTED_COUNTRIES}
						label={t(GENERAL_FORM_FIELDS_NAMES.SUPPORTED_COUNTRIES)}
						placeholder={t(GENERAL_FORM_FIELDS_NAMES.SUPPORTED_COUNTRIES)}
						inputType={InputType.MultiSelect}
						data={merchantsLists?.countries}
						ref={supportedCountriesRef}
						allValue={merchantsLists?.countries?.map(item => item.value)}
						isEditMode={isEditMode}
						labelAdditionalClasses="fs-7"
						fakeInputLabelAdditionalClasses="fs-7"
						additionalClasses="col-12 col-sm-12 col-md-12 supported-countries-selector"
						fakeInputAdditionalClass="col-md-12"
						isLabelIcon
						tooltipText={t(GENERAL_FORM_FIELDS_NAMES.SUPPORTED_COUNTRIES_DESCRIPTION)}
						key={GENERAL_FORM_FIELDS.SUPPORTED_COUNTRIES}
					/>
				);
			default:
				return (
					<GeneralSwitches
						formik={formik}
						formEditStatus={isEditMode}
						title={title}
						formField={formName}
						tooltip={t(description)}
					/>
				);
		}
	};

	return (
		<div className="col-12 col-md-6 col-xxl-4 pb-5 ps-5 test">
			<div className="general-category-card p-5 h-100">
				{title && <Title title={`${t(title)}:`} additionalClass="fs-6 fw-bolder mb-5"/>}
				{
					items.map(({ title, formName, description }, index) => {
						// Create a string key that's guaranteed to be a string
						const itemKey = `item-${index}-${String(formName)}`;
						return (
							<Fragment key={itemKey}>
								{getSwitcher(title, formName, description)}
							</Fragment>
						);
					})
				}
			</div>
		</div>
	);
};
